// ArchitectAudit — /architect/audit (v19.16)
//
// Port of the Pythonista `run_security_audit()` scoreboard into a live
// dashboard. Architect-only. Reads /api/architect/audit which rolls
// up the HMAC-sealed `never_harm_events` table into:
//   • Total threats stopped, plus 24h / 7d / 30d buckets
//   • Top recurring attacks by payload_hash (content-hash dedup)
//   • Recent 30 trapped payloads with short-ID + origin + gate
//
// Every payload gets a 16-char content-hash so the same attack fired
// from ten different sessions collapses to one row in Top Recurring —
// exactly the "pattern-of-life" property the Pythonista prototype had
// with sha256(payload)[:8].

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck, Copy, Check, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getArchitectAudit } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function Stat({ label, value, tone = "zinc", testid }) {
  const tones = {
    zinc:    "border-zinc-800 text-zinc-100",
    amber:   "border-amber-500/40 text-amber-200",
    rose:    "border-rose-500/40 text-rose-200",
    emerald: "border-emerald-500/40 text-emerald-200",
  };
  return (
    <div className={`border ${tones[tone] || tones.zinc} bg-zinc-950/60 p-4`} data-testid={testid}>
      <div className="text-[9.5px] uppercase tracking-[0.28em] text-zinc-500 font-mono">
        {label}
      </div>
      <div className="text-3xl font-light mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function HashChip({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === "—") {
    return <span className="text-[10px] font-mono text-zinc-600">—</span>;
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-300/85 hover:text-amber-200"
      title={`payload_hash · ${value}`}
    >
      <span>{value.slice(0, 10)}…</span>
      {copied ? <Check className="h-3 w-3 text-emerald-300" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

export default function ArchitectAudit() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getArchitectAudit(40, 20);
      setData(r);
      setErr(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : d?.message || "Failed to load audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  if (!authLoading && !user?.is_architect) {
    return (
      <div className="min-h-screen bg-black text-zinc-200 p-12 max-w-2xl mx-auto">
        <div className="border border-rose-500/40 bg-rose-500/[0.03] p-6" data-testid="audit-gate">
          <div className="text-[10px] uppercase tracking-[0.35em] text-rose-300 font-mono flex items-center gap-2">
            <AlertTriangle className="h-3 w-3" /> architect only
          </div>
          <div className="text-lg text-zinc-100 mt-2">
            This audit surface is sealed to the Architect.
          </div>
          <Link to="/" className="mt-4 inline-block text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-300 font-mono">
            ← back to console
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-200 py-10 px-6 sm:px-10 max-w-6xl mx-auto" data-testid="architect-audit-page">
      <header className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.35em] text-amber-300 font-mono inline-flex items-center gap-2">
          <ShieldCheck className="h-3 w-3" /> projectivy master security audit
        </div>
        <div className="flex items-start justify-between gap-4 mt-2">
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-zinc-100">
            Every threat the Sentinel has trapped.
          </h1>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="audit-refresh"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-800 hover:border-amber-500/40 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-amber-300 font-mono disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            refresh
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-3 leading-relaxed max-w-2xl">
          HMAC-sealed rollup of every never-harm event across the safety
          pipeline — regex anchors, encoded-payload defense, prompt-intent
          classifier, session boil-the-frog, Northern Light lockouts.
          Content-hashed so identical attacks from different origins
          collapse into one row for pattern-of-life analysis.
        </p>
      </header>

      {err && !loading && (
        <div className="border border-rose-500/40 bg-rose-500/[0.03] p-4 mb-6 text-sm text-rose-200 font-mono">
          {err}
        </div>
      )}

      {loading && !data && (
        <div className="text-center py-14 text-[11px] uppercase tracking-[0.3em] font-mono text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> loading audit…
        </div>
      )}

      {data && (
        <>
          {/* STATS */}
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="audit-stats">
            <Stat label="threats · all-time" value={data.stats.total} testid="stat-total" tone="amber" />
            <Stat label="last 24h" value={data.stats.last_24h} testid="stat-24h" tone={data.stats.last_24h > 0 ? "rose" : "emerald"} />
            <Stat label="last 7 days" value={data.stats.last_7d} testid="stat-7d" />
            <Stat label="last 30 days" value={data.stats.last_30d} testid="stat-30d" />
          </section>

          {/* TOP RECURRING */}
          <section className="mb-10" data-testid="audit-top-recurring-section">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-[11px] uppercase tracking-[0.35em] text-zinc-500 font-mono">
                ⚠ top recurring · pattern-of-life
              </h2>
              <span className="text-[10px] font-mono text-zinc-600">
                {data.top_recurring.length} unique attack signatures
              </span>
            </div>

            {data.top_recurring.length === 0 ? (
              <div className="border border-zinc-800 bg-zinc-950 p-6 text-center text-zinc-500 text-sm">
                No recurring attacks yet.
              </div>
            ) : (
              <div className="border border-zinc-800 bg-zinc-950/50 divide-y divide-zinc-900">
                {data.top_recurring.map((r) => (
                  <div
                    key={r.payload_hash}
                    className="p-4 grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-3 items-start"
                    data-testid={`recurring-row-${r.payload_hash}`}
                  >
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-2xl font-light tabular-nums text-rose-300 min-w-[3ch] text-right">
                        ×{r.count}
                      </div>
                      <HashChip value={r.payload_hash} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-zinc-300 font-mono break-words">
                        {r.sample_trigger}
                      </div>
                      <div className="text-[10px] text-zinc-500 font-mono mt-1">
                        gate: <span className="text-amber-300/80">{r.sample_gate || "—"}</span>
                        {"  ·  "}
                        first seen {new Date(r.first_seen).toLocaleDateString()}
                        {r.first_seen !== r.last_seen && (
                          <> · last {new Date(r.last_seen).toLocaleDateString()}</>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* RECENT */}
          <section data-testid="audit-recent-section">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-[11px] uppercase tracking-[0.35em] text-zinc-500 font-mono">
                ▤ recent trapped payloads
              </h2>
              <span className="text-[10px] font-mono text-zinc-600">
                {data.recent.length} shown
              </span>
            </div>

            {data.recent.length === 0 ? (
              <div className="border border-zinc-800 bg-zinc-950 p-6 text-center text-zinc-500 text-sm">
                Ledger empty — no threats have been neutralized yet.
              </div>
            ) : (
              <div className="border border-zinc-800 bg-zinc-950/50 divide-y divide-zinc-900" data-testid="recent-list">
                {data.recent.map((e) => (
                  <div
                    key={e.event_id}
                    className="p-3 grid grid-cols-[auto_1fr] gap-3 items-start"
                    data-testid={`recent-row-${e.event_id}`}
                  >
                    <div className="flex flex-col gap-1 shrink-0 w-[10rem]">
                      <HashChip value={e.payload_hash} />
                      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-600 font-mono">
                        {e.gate || "—"}
                      </div>
                      <div className="text-[9px] font-mono text-zinc-600">
                        {new Date(e.timestamp).toLocaleString(undefined, {
                          month: "short", day: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-zinc-300 font-mono break-words">
                        {e.trigger}
                      </div>
                      {e.matched && (
                        <div className="text-[10px] font-mono text-rose-300/70 mt-1">
                          matched: {e.matched}
                        </div>
                      )}
                      <div className="text-[9px] font-mono text-zinc-600 mt-1">
                        origin: {String(e.origin).slice(0, 32)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <footer className="mt-10 border-t border-zinc-900 pt-4 text-[10px] font-mono text-zinc-600 flex items-center justify-between">
            <span>generated {new Date(data.generated_at).toLocaleString()}</span>
            {data.backfilled_this_call > 0 && (
              <span className="text-amber-300/80">
                backfilled {data.backfilled_this_call} pre-v19.16 rows with payload_hash
              </span>
            )}
          </footer>
        </>
      )}
    </div>
  );
}
